import React, { useEffect, useState } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const api = (path) => `${BACKEND_URL}/api/admin/stripe-accounts${path}`;

function authHeaders() {
  return { Authorization: `Bearer ${Cookies.get("token")}` };
}

function apiErrorDetail(err, fallback = "Request failed") {
  const d = err?.response?.data?.detail;
  if (typeof d === "string" && d) return d;
  if (Array.isArray(d) && d.length) {
    const first = d[0];
    if (typeof first === "string") return first;
    if (first?.msg) return String(first.msg);
  }
  return fallback;
}

const emptyForm = () => ({
  name: "",
  publishable_key: "",
  secret_key: "",
  webhook_secret: "",
  is_primary: false,
});

const AdminStripeAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const selected = accounts.find((acc) => acc.id === selectedId) || null;
  const isEdit = selectedId !== null;

  const loadAccounts = async () => {
    setLoading(true);
    try {
      const res = await axios.get(api(""), { headers: authHeaders() });
      setAccounts(res.data.accounts || []);
    } catch (err) {
      toast.error(apiErrorDetail(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAccounts();
  }, []);

  const resetCreateForm = () => {
    setSelectedId(null);
    setForm(emptyForm());
  };

  const selectAccount = (acc) => {
    setSelectedId(acc.id);
    setForm({
      name: acc.name || "",
      publishable_key: acc.publishable_key || "",
      secret_key: "",
      webhook_secret: "",
      is_primary: false,
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (isEdit) {
        await axios.patch(
          api("/" + selectedId),
          {
            name: form.name,
            publishable_key: form.publishable_key,
            secret_key: form.secret_key,
            webhook_secret: form.webhook_secret,
          },
          { headers: authHeaders() }
        );
        await loadAccounts();
        setForm((prev) => ({ ...prev, secret_key: "", webhook_secret: "" }));
      } else {
        await axios.post(
          api(""),
          {
            name: form.name,
            publishable_key: form.publishable_key,
            secret_key: form.secret_key,
            webhook_secret: form.webhook_secret,
            is_primary: form.is_primary,
          },
          { headers: authHeaders() }
        );
        await loadAccounts();
        resetCreateForm();
      }
    } catch (err) {
      toast.error(apiErrorDetail(err));
    } finally {
      setSaving(false);
    }
  };

  const handleVerify = async (id) => {
    setSaving(true);
    try {
      await axios.post(api("/" + id + "/verify"), {}, { headers: authHeaders() });
      await loadAccounts();
    } catch (err) {
      toast.error(apiErrorDetail(err));
    } finally {
      setSaving(false);
    }
  };

  const handleMakePrimary = async (acc) => {
    if (
      !window.confirm(
        `Make "${acc.name}" the primary Stripe account? New checkouts will use this account only after the billing catalog step.`
      )
    ) {
      return;
    }
    setSaving(true);
    try {
      await axios.post(api("/" + acc.id + "/make-primary"), {}, { headers: authHeaders() });
      await loadAccounts();
    } catch (err) {
      toast.error(apiErrorDetail(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (acc) => {
    if (!window.confirm(`Delete Stripe account "${acc.name}"? This cannot be undone.`)) {
      return;
    }
    setSaving(true);
    try {
      await axios.delete(api("/" + acc.id), { headers: authHeaders() });
      await loadAccounts();
      resetCreateForm();
    } catch (err) {
      toast.error(apiErrorDetail(err));
    } finally {
      setSaving(false);
    }
  };

  const copyWebhookUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Webhook URL copied");
    } catch {
      toast.error("Could not copy");
    }
  };

  const inputClass =
    "w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-sm text-[#F3F4F6] placeholder:text-[#6B7280] focus:outline-none focus:border-[#00D1FF]/50";

  return (
    <div data-testid="admin-stripe-accounts">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold mb-2">Stripe</h1>
          <p className="text-[#9CA3AF] text-sm">Manage Stripe accounts used for billing</p>
        </div>
        <button
          type="button"
          onClick={resetCreateForm}
          className="px-4 py-2 rounded-lg border border-white/10 text-sm text-[#F3F4F6] hover:bg-white/5"
        >
          New account
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-3">
          {loading ? (
            <p className="text-[#9CA3AF]">Loading…</p>
          ) : accounts.length === 0 ? (
            <p className="text-[#9CA3AF]">No Stripe accounts yet</p>
          ) : (
            accounts.map((acc) => (
              <div
                key={acc.id}
                role="button"
                tabIndex={0}
                onClick={() => selectAccount(acc)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") selectAccount(acc);
                }}
                className={`w-full text-left rounded-xl border p-4 bg-white/[0.03] transition-colors cursor-pointer ${
                  selectedId === acc.id
                    ? "border-[#00D1FF]/40"
                    : "border-white/10 hover:border-white/20"
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-heading text-[#F3F4F6]">{acc.name}</span>
                  {acc.is_primary && (
                    <span className="text-xs px-2 py-0.5 rounded bg-[#00D1FF]/10 text-[#00D1FF] border border-[#00D1FF]/20">
                      Primary
                    </span>
                  )}
                </div>
                <p className="text-xs text-[#9CA3AF] mb-1">
                  Secret key: {acc.secret_key_set ? "Set" : "Not set"} · Webhook secret:{" "}
                  {acc.webhook_secret_set ? "Set" : "Not set"}
                </p>
                {acc.publishable_key && (
                  <p className="text-xs text-[#F3F4F6] break-all mb-1">{acc.publishable_key}</p>
                )}
                <div className="flex items-center gap-2">
                  <p className="text-xs text-[#9CA3AF] break-all flex-1">{acc.webhook_url}</p>
                  <span
                    role="button"
                    tabIndex={0}
                    className="text-xs text-[#00D1FF] shrink-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      copyWebhookUrl(acc.webhook_url);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.stopPropagation();
                        copyWebhookUrl(acc.webhook_url);
                      }
                    }}
                  >
                    Copy
                  </span>
                </div>
                <p className="text-xs text-[#9CA3AF] mt-2">
                  Paste this URL and this account’s signing secret into this Stripe Dashboard only.
                </p>
                {!acc.webhook_secret_set && (
                  <p className="text-xs text-[#9CA3AF] mt-2">
                    Events for this account will not be accepted until you paste the signing secret (whsec_) for this Dashboard.
                  </p>
                )}
                <p className="text-xs text-[#9CA3AF] mt-2">
                  {acc.last_verified_at || "Not verified"}
                </p>
              </div>
            ))
          )}
        </div>

        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5 space-y-4">
          <h2 className="font-heading text-lg text-[#F3F4F6]">
            {isEdit ? "Edit account" : "Create account"}
          </h2>
          <label className="block text-sm text-[#9CA3AF]">
            Name
            <input
              className={`${inputClass} mt-1`}
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            />
          </label>
          <label className="block text-sm text-[#9CA3AF]">
            Publishable key
            <input
              className={`${inputClass} mt-1`}
              value={form.publishable_key}
              onChange={(e) => setForm((prev) => ({ ...prev, publishable_key: e.target.value }))}
              placeholder={isEdit ? "Leave blank to keep" : ""}
            />
          </label>
          <label className="block text-sm text-[#9CA3AF]">
            Secret key
            <input
              type="password"
              className={`${inputClass} mt-1`}
              value={form.secret_key}
              onChange={(e) => setForm((prev) => ({ ...prev, secret_key: e.target.value }))}
              placeholder={isEdit ? "Leave blank to keep" : ""}
            />
          </label>
          <label className="block text-sm text-[#9CA3AF]">
            Webhook secret
            <input
              type="password"
              className={`${inputClass} mt-1`}
              value={form.webhook_secret}
              onChange={(e) => setForm((prev) => ({ ...prev, webhook_secret: e.target.value }))}
              placeholder={isEdit ? "Leave blank to keep" : ""}
            />
          </label>
          {!isEdit && (
            <label className="flex items-center gap-2 text-sm text-[#F3F4F6]">
              <input
                type="checkbox"
                checked={form.is_primary}
                onChange={(e) => setForm((prev) => ({ ...prev, is_primary: e.target.checked }))}
              />
              Make primary
            </label>
          )}
          <button
            type="button"
            disabled={saving}
            onClick={handleSave}
            className="px-4 py-2 rounded-lg bg-[#00D1FF]/10 text-[#00D1FF] border border-[#00D1FF]/20 text-sm disabled:opacity-50"
          >
            Save
          </button>
          {isEdit && selected && (
            <div className="flex flex-col gap-3 pt-2 border-t border-white/10">
              <button
                type="button"
                disabled={saving || !selected.secret_key_set}
                onClick={() => handleVerify(selected.id)}
                className="px-4 py-2 rounded-lg border border-white/10 text-sm text-[#F3F4F6] disabled:opacity-50"
              >
                Check key
              </button>
              <button
                type="button"
                disabled={saving || selected.is_primary}
                onClick={() => handleMakePrimary(selected)}
                className="px-4 py-2 rounded-lg border border-white/10 text-sm text-[#F3F4F6] disabled:opacity-50"
              >
                Make primary
              </button>
              <p className="text-xs text-[#9CA3AF]">
                New checkouts will use this account only after the billing catalog step. Price IDs
                in the matrix must be from this Dashboard.
              </p>
              <button
                type="button"
                disabled={saving}
                onClick={() => handleDelete(selected)}
                className="px-4 py-2 rounded-lg border border-red-500/30 text-sm text-red-400 disabled:opacity-50"
              >
                Delete
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminStripeAccounts;
